import * as theme from "../theme";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Button from "../components/CustomButton";
import Context from "../contexts/data";
import CustomInput from "../components/inputs/CustomInput";
import ErrorMessage from "../components/ErrorMessage";
import HeaderText from "../components/HeaderText";
import InputContainer from "../components/inputs/InputContainer";
import RateZipSlider from "../components/StyledSlider";
import { checkValid } from "../util/reverseRequiredInfo";
import { formatCurrency } from "../util/regex";
import { handleRedirection } from "../util/httpHelpers";
import styled, { keyframes } from "styled-components";

const HomeAmount = () => {
  const { homeValue, errorMessage, handleUpdateFormValue, navigate } =
    React.useContext(Context);

  const [homeValueText, setHomeValueText] = useState(formatCurrency(homeValue));
  const [validating, setValidating] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ageParam = searchParams.get("age");
    const zipParam = searchParams.get("zip");

    if (!ageParam || !zipParam) return;

    setValidating(true);

    const runValidation = async () => {
      const parsedAge = parseInt(ageParam, 10);

      if (isNaN(parsedAge) || parsedAge < 62) {
        handleRedirection("age");
        return;
      }

      const { isValidState, redirect } = await checkValid(
        handleUpdateFormValue,
        zipParam
      );

      if (!isValidState) {
        handleRedirection(redirect ? "state" : "age");
        return;
      }

      handleUpdateFormValue("age", String(parsedAge));
      handleUpdateFormValue("zipCode", zipParam);
      setValidating(false);
    };

    runValidation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextStep = () => {
    if (homeValue > 2000000) {
      handleUpdateFormValue(
        "errorMessage",
        "Select a home value less than or equal to $2,000,000."
      );
      return;
    } else if (homeValue < 100000) {
      handleUpdateFormValue(
        "errorMessage",
        "Select a home value greater than or equal to $100,000."
      );
    } else if (homeValue < 200000) {
      handleRedirection("homeValue");
    } else {
      const updatedMortgageBalance = Math.floor(homeValue * 0.25);
      handleUpdateFormValue("errorMessage", "");
      handleUpdateFormValue("mortgageBalance", updatedMortgageBalance);

      navigate("mortgage-balance", "Home Value");
    }
  };

  const handleChange = ({ target }) => {
    const { value } = target;
    const { textMoney, intMoney } = formatCurrency(value, "both");

    if (intMoney !== 0) {
      setHomeValueText(textMoney);
      handleUpdateFormValue("homeValue", intMoney);
    } else {
      setHomeValueText("");
      handleUpdateFormValue("homeValue", "");
    }
  };

  if (validating) {
    return (
      <LoadingWrapper>
        <Spinner />
        <LoadingText>Loading Secure Form...</LoadingText>
      </LoadingWrapper>
    );
  }

  return (
    <Wrapper>
      <HeaderText bigger_text={"Estimated home value"} />
      <RateZipSlider
        range="$100,000 - $2,000,000+"
        min={100000}
        max={2000000}
        value={homeValue}
        handleChange={handleChange}
        step={50000}
      />
      <InputContainer inputClass={"genericContainer"}>
        <CustomInput
          value={"$" + homeValueText}
          errorMessage={errorMessage}
          fieldClass="numberInput"
          onChange={handleChange}
          name={"Home Value"}
          labelName={"Home Value"}
        />
      </InputContainer>
      <ErrorMessage />
      <Button onClick={handleNextStep} />
    </Wrapper>
  );
};

const Wrapper = styled(theme.column_align)`
  @media (max-width: ${theme.mobile}) {
    margin-bottom: 16px;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingWrapper = styled(theme.column_align)`
  min-height: 200px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: ${theme.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: 16px;
`;

const LoadingText = styled.p`
  font-family: "Inter";
  font-size: 16px;
  font-weight: 500;
  color: ${theme.primary};
`;

export default HomeAmount;
